import React, { useState } from 'react';
import { Button } from './ui/button';
import { Send, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ContactSection = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (!res.ok) throw new Error('Failed');
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('error');
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30 transition-colors";

  return (
    <section id="contact" className="bg-gradient-to-b from-[#0a0e27] to-[#050811] py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 data-testid="contact-title" className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.contact.title}
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto">
            {t.contact.subtitle}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6">
            <a
              href={`tel:${t.contact.phone.replace(/\s/g, '')}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-2 text-white/80 hover:text-cyan-400 transition-colors"
            >
              <Phone size={18} className="text-cyan-400" />
              <span className="text-sm">
                <span className="text-white/50 uppercase tracking-wide text-xs mr-2">{t.contact.phoneLabel}:</span>
                {t.contact.phone}
              </span>
            </a>
            <a
              href={`mailto:${t.contact.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-2 text-white/80 hover:text-cyan-400 transition-colors"
            >
              <Mail size={18} className="text-cyan-400" />
              <span className="text-sm">
                <span className="text-white/50 uppercase tracking-wide text-xs mr-2">{t.contact.emailLabel}:</span>
                {t.contact.email}
              </span>
            </a>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="contact-name"
              type="text"
              name="name"
              required
              placeholder={t.contact.name}
              value={form.name}
              onChange={handleChange}
              className={inputClass}
            />
            <input
              data-testid="contact-email"
              type="email"
              name="email"
              required
              placeholder={t.contact.email_placeholder}
              value={form.email}
              onChange={handleChange}
              className={inputClass}
            />
          </div>
          <input
            data-testid="contact-subject"
            type="text"
            name="subject"
            placeholder={t.contact.subject}
            value={form.subject}
            onChange={handleChange}
            className={inputClass}
          />
          <textarea
            data-testid="contact-message"
            name="message"
            required
            rows={5}
            placeholder={t.contact.message}
            value={form.message}
            onChange={handleChange}
            className={`${inputClass} resize-none`}
          />

          <div className="text-center">
            <Button
              data-testid="contact-submit"
              type="submit"
              size="lg"
              disabled={status === 'sending'}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-10"
            >
              {status === 'sending' ? t.contact.sending : t.contact.send}
              <Send className="ml-2" size={18} />
            </Button>
          </div>

          {status === 'success' && (
            <p data-testid="contact-success" className="text-center text-emerald-400 text-sm mt-4">{t.contact.success}</p>
          )}
          {status === 'error' && (
            <p data-testid="contact-error" className="text-center text-red-400 text-sm mt-4">{t.contact.error}</p>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
